import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Car, FileSignature, Siren, UserRound,
  UploadCloud, CheckCircle2, AlertCircle, Info, Calendar, Home, Building2, PawPrint, TreePine, ShoppingBag,
} from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const SERVICE_CATEGORIES = [
  { id: "home",        label: "Home Cleaning",         icon: Home       },
  { id: "commercial",  label: "Commercial Cleaning",   icon: Building2  },
  { id: "car",         label: "Car Cleaning",          icon: Car        },
  { id: "pet",         label: "Pet Care",              icon: PawPrint   },
  { id: "outdoor",     label: "Outdoor Cleaning",      icon: TreePine   },
  { id: "convenience", label: "Convenience Services",  icon: ShoppingBag},
];

const CAR_STATUS_BADGE = {
  approved:    { label: "Approved for Car",   cls: "bg-amber-100 text-amber-900 border-amber-300" },
  pending:     { label: "Pending admin review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  rejected:    { label: "Rejected",           cls: "bg-red-100 text-red-700 border-red-200" },
  not_applied: { label: "Not applied",        cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const TRANSPORT_LABELS = {
  own_vehicle: "My own vehicle",
  public_transit: "Public transit",
  walk: "Walk",
  bike: "Bicycle",
  rideshare: "Rideshare / taxi",
  other: "Other",
};

const INS_STATUS = {
  pending_review: { label: "Pending review", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  approved:       { label: "Approved",       cls: "bg-teal-100 text-teal-800 border-teal-200" },
  rejected:       { label: "Rejected",       cls: "bg-red-100 text-red-700 border-red-200" },
  needs_resubmission: { label: "Resubmit",   cls: "bg-orange-100 text-orange-800 border-orange-200" },
  expired:        { label: "Expired",        cls: "bg-red-100 text-red-700 border-red-200" },
};

function Requirement({ ok, label, sub }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-white">
      {ok ? (
        <CheckCircle2 className="w-5 h-5 text-teal-600 mt-0.5" />
      ) : (
        <ShieldAlert className="w-5 h-5 text-amber-500 mt-0.5" />
      )}
      <div>
        <div className={`font-medium ${ok ? "text-slate-700" : "text-amber-900"}`}>{label}</div>
        {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

export default function CleanerRiskCenter() {
  const { user } = useApp();
  const nav = useNavigate();
  const [eligibility, setEligibility] = useState(null);
  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [carAck, setCarAck] = useState(false);
  const [savingCats, setSavingCats] = useState(false);
  const [emergency, setEmergency] = useState({ name: "", relationship: "", phone: "", email: "" });
  const [vehicle, setVehicle] = useState({
    transportation_type: "", has_vehicle: false, vehicle_type: "", year: "", make: "", model: "",
    color: "", plate_state: "", license_plate: "", vin_last6: "",
    dl_state: "", dl_expiration_date: "", dl_last4: "",
    dl_front_url: "", dl_back_url: "", dl_status: "",
  });
  const [insurance, setInsurance] = useState([]);
  const [insForm, setInsForm] = useState({
    insurance_type: "auto", carrier: "", policy_number: "",
    effective_date: "", expiration_date: "", document_url: "", notes: "",
    insurance_state: "",
    understands_business_use_ack: false,
    maintains_required_coverage_ack: false,
    attest_current_accurate: false,
  });
  const [agreement, setAgreement] = useState(null);
  const [agreementStatus, setAgreementStatus] = useState({ accepted: false });
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [incidentForm, setIncidentForm] = useState({
    kind: "vehicle_accident", severity: "medium", job_id: "",
    occurred_at: new Date().toISOString().slice(0, 16),
    location: "", description: "",
    involved_parties: "", police_report_number: "", insurance_claim_number: "",
  });
  const [uploadingFor, setUploadingFor] = useState(null);

  const load = async () => {
    try {
      const [e, ec, v, ins, agr, agrSt, inc, pr] = await Promise.all([
        api.get("/cleaner/eligibility"),
        api.get("/cleaner/emergency-contact"),
        api.get("/cleaner/vehicle"),
        api.get("/cleaner/insurance"),
        api.get("/cleaner/agreement/latest"),
        api.get("/cleaner/agreement/status"),
        api.get("/cleaner/incidents"),
        api.get("/cleaner/profile"),
      ]);
      setEligibility(e.data);
      if (ec.data && ec.data.name) setEmergency(ec.data);
      if (v.data && v.data.transportation_type) setVehicle({ ...vehicle, ...v.data });
      setInsurance(ins.data || []);
      setAgreement(agr.data);
      setAgreementStatus(agrSt.data);
      setIncidents(inc.data || []);
      setProfile(pr.data || null);
      setCategories(pr.data?.service_categories || []);
      setCarAck(!!pr.data?.car_equipment_ack);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load risk profile");
    }
  };
  useEffect(() => { load(); }, []);

  const toggleCategory = (id) => {
    setCategories((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };
  const saveCategories = async () => {
    if (categories.includes("car") && !carAck) {
      toast.error("Please confirm you have the equipment for Car Cleaning before saving.");
      return;
    }
    setSavingCats(true);
    try {
      const r = await api.put("/cleaner/profile", {
        service_categories: categories,
        car_equipment_ack: carAck,
      });
      setProfile(r.data);
      toast.success(
        categories.includes("car") && (r.data?.car_cleaning_status === "pending" || !profile?.service_categories?.includes("car"))
          ? "Saved. Car Cleaning application sent for admin review."
          : "Service categories saved"
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingCats(false);
    }
  };

  const saveEmergency = async () => {
    if (!emergency.name || !emergency.phone) return toast.error("Name and phone are required");
    try {
      await api.put("/cleaner/emergency-contact", emergency);
      toast.success("Emergency contact saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const saveVehicle = async () => {
    if (!vehicle.transportation_type) return toast.error("Choose your transportation type");
    const payload = { ...vehicle };
    payload.year = payload.year ? parseInt(payload.year, 10) : null;
    if (!payload.year) delete payload.year;
    try {
      await api.put("/cleaner/vehicle", payload);
      toast.success("Transportation saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const submitInsurance = async () => {
    const f = insForm;
    if (!f.carrier || !f.policy_number || !f.expiration_date || !f.document_url) {
      return toast.error("Fill carrier, policy number, expiration date, and upload the document");
    }
    if (!f.attest_current_accurate) {
      return toast.error("Please confirm the insurance information and proof are current and accurate.");
    }
    if (!f.understands_business_use_ack || !f.maintains_required_coverage_ack) {
      return toast.error("Please acknowledge the business-use and coverage-maintenance statements.");
    }
    try {
      await api.post("/cleaner/insurance", f);
      toast.success("Insurance submitted for review");
      setInsForm({ insurance_type: "auto", carrier: "", policy_number: "",
                   effective_date: "", expiration_date: "", document_url: "", notes: "",
                   insurance_state: "",
                   understands_business_use_ack: false,
                   maintains_required_coverage_ack: false,
                   attest_current_accurate: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
  };

  const acceptAgreement = async () => {
    try {
      await api.post("/cleaner/agreement/accept", { version: agreement.version, accepted: true });
      toast.success("Agreement accepted");
      setAgreementOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to record acceptance"); }
  };

  const submitIncident = async () => {
    const f = incidentForm;
    if (!f.description || f.description.length < 10) return toast.error("Describe the incident (10+ characters)");
    try {
      await api.post("/cleaner/incidents", { ...f, occurred_at: new Date(f.occurred_at).toISOString() });
      toast.success("Incident reported — CLEANBAZ admin has been notified");
      setIncidentOpen(false);
      setIncidentForm({ ...incidentForm, description: "", location: "", involved_parties: "", police_report_number: "", insurance_claim_number: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
  };

  const handleUpload = async (event, target) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingFor(target);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url || data.path;
      if (target === "insurance") setInsForm(s => ({ ...s, document_url: url }));
      if (target === "dl_front") setVehicle(s => ({ ...s, dl_front_url: url }));
      if (target === "dl_back") setVehicle(s => ({ ...s, dl_back_url: url }));
      toast.success("File uploaded — remember to submit");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingFor(null);
    }
  };

  if (!eligibility) {
    return <div className="max-w-3xl mx-auto p-12 text-slate-500" data-testid="risk-loading">Loading risk profile…</div>;
  }

  const e = eligibility;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="cleaner-risk-center">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest text-teal-600">CLEANBAZ · Safety & Compliance</div>
        <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1">Onboarding & Risk Checklist</h1>
        <p className="text-slate-600 mt-2">Complete each item below to unlock job acceptance. Sensitive documents are visible only to CLEANBAZ authorised admins.</p>
      </div>

      {/* Service Categories the provider wants to offer.
          Approval for "car" is tracked separately via car_cleaning_status. */}
      <Card className="mb-6 border-slate-200" data-testid="service-categories-card">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-teal-600">Service categories</div>
              <div className="font-display font-bold text-xl text-slate-900 mt-1">Which services do you want to offer?</div>
              <p className="text-sm text-slate-600 mt-1">
                Pick every category you can provide. Car Cleaning is reviewed separately — admin will approve or reject it
                based on your vehicle, driver's license and current auto insurance already on file.
              </p>
            </div>
            {profile?.service_categories?.includes("car") && (
              <Badge
                data-testid="car-status-badge"
                className={`text-[10px] font-mono uppercase tracking-widest border ${
                  CAR_STATUS_BADGE[profile?.car_cleaning_status || "not_applied"]?.cls || CAR_STATUS_BADGE.not_applied.cls
                }`}
              >
                {CAR_STATUS_BADGE[profile?.car_cleaning_status || "not_applied"]?.label || "Not applied"}
              </Badge>
            )}
          </div>

          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {SERVICE_CATEGORIES.map((c) => {
              const active = categories.includes(c.id);
              const Icon = c.icon;
              return (
                <label
                  key={c.id}
                  data-testid={`service-cat-${c.id}`}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    active ? "border-teal-500 bg-teal-50" : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggleCategory(c.id)}
                    data-testid={`service-cat-toggle-${c.id}`}
                    className="w-4 h-4 accent-teal-500"
                  />
                  <Icon className={`w-4 h-4 ${active ? "text-teal-700" : "text-slate-500"}`} />
                  <span className="text-sm font-semibold text-slate-800">{c.label}</span>
                </label>
              );
            })}
          </div>

          {categories.includes("car") && (
            <div className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <Car className="w-5 h-5 text-amber-700 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-amber-900">Car Cleaning eligibility requires:</div>
                  <ul className="mt-1.5 text-sm text-amber-900 space-y-0.5 list-disc pl-5">
                    <li>A vehicle on file (see Transportation section below)</li>
                    <li>Valid driver's license</li>
                    <li>Current auto insurance uploaded &amp; approved</li>
                    <li>Vehicle information (year / make / model / plate) complete</li>
                  </ul>
                  <label className="mt-3 flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={carAck}
                      onChange={(e) => setCarAck(e.target.checked)}
                      data-testid="car-equipment-ack"
                      className="w-4 h-4 mt-0.5 accent-amber-600"
                    />
                    <span className="text-sm text-amber-900">
                      I confirm I have the appropriate equipment and ability to perform the selected car-cleaning services
                      (vacuum, brushes, cleaning supplies, water access or waterless system).
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              {profile?.car_cleaning_status === "rejected" && profile?.car_cleaning_reason && (
                <>Admin note: <span className="text-red-700">{profile.car_cleaning_reason}</span></>
              )}
            </div>
            <Button
              onClick={saveCategories}
              disabled={savingCats}
              data-testid="save-service-categories"
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {savingCats ? "Saving..." : "Save service categories"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Overall eligibility summary */}
      <Card className={`mb-6 ${e.eligible_for_transport_jobs ? "border-teal-300 bg-teal-50" : "border-amber-300 bg-amber-50"}`} data-testid="eligibility-summary">
        <CardContent className="p-5 flex items-start gap-3">
          {e.eligible_for_transport_jobs ? (
            <ShieldCheck className="w-6 h-6 text-teal-600 mt-1" />
          ) : (
            <ShieldAlert className="w-6 h-6 text-amber-600 mt-1" />
          )}
          <div className="flex-1">
            <div className={`font-semibold ${e.eligible_for_transport_jobs ? "text-teal-900" : "text-amber-900"}`}>
              {e.eligible_for_transport_jobs
                ? "You're cleared for all CLEANBAZ jobs"
                : "You cannot accept jobs yet — complete the items below"}
            </div>
            <div className={`text-sm ${e.eligible_for_transport_jobs ? "text-teal-800" : "text-amber-800"} mt-1`}>
              Identity <b>{e.identity_status}</b> · Agreement <b>{e.agreement_ok ? "accepted" : "not accepted"}</b>
              {" · "}Transport <b>{TRANSPORT_LABELS[e.transportation_type] || "not declared"}</b>
              {e.auto_insurance_required && (
                <>{" · Auto insurance "}<b>{e.auto_insurance_active ? `valid until ${e.auto_insurance_expiration}` : "missing/expired"}</b></>
              )}
              {e.drivers_license_required && (
                <>{" · Driver's license "}<b data-testid="dl-summary">{e.drivers_license_active ? `${e.drivers_license_status} · until ${e.drivers_license_expiration}` : `${e.drivers_license_status || "missing"}`}</b></>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requirement checklist */}
      <div className="grid md:grid-cols-2 gap-3 mb-8" data-testid="requirement-grid">
        <Requirement ok={e.identity_ok} label="Government ID verified" sub={`Status: ${e.identity_status}`} />
        <Requirement ok={e.agreement_ok} label="CLEANBAZ Cleaner Agreement accepted" sub={agreementStatus.accepted ? `Version ${agreementStatus.current_version}` : "Not accepted yet"} />
        <Requirement ok={e.emergency_contact_ok} label="Emergency contact on file" sub="Required for safety incident response" />
        <Requirement ok={!!e.transportation_type} label="Transportation type declared" sub={TRANSPORT_LABELS[e.transportation_type] || "Not declared"} />
        {e.transportation_type === "own_vehicle" && (
          <>
            <Requirement ok={e.vehicle_info_ok} label="Vehicle information complete" sub="Type, year, make, plate & state required" />
            <Requirement ok={e.drivers_license_active} label="Valid driver's license on file" sub={e.drivers_license_active ? `Verified · expires ${e.drivers_license_expiration}` : `Status: ${e.drivers_license_status || "not submitted"}${e.drivers_license_expiration ? ` · expires ${e.drivers_license_expiration}` : ""}`} />
            <Requirement ok={e.auto_insurance_active} label="Valid auto insurance on file" sub={e.auto_insurance_active ? `Valid until ${e.auto_insurance_expiration}` : "Missing, expired, or not approved"} />
          </>
        )}
        {e.transportation_type && e.transportation_type !== "own_vehicle" && (
          <div className="md:col-span-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm">
            <b>Note:</b> With transport type "{TRANSPORT_LABELS[e.transportation_type]}", you can only accept jobs an admin has marked as not requiring transportation. Most CLEANBAZ jobs require a vehicle to transport supplies.
          </div>
        )}
      </div>

      <Tabs defaultValue="agreement" data-testid="risk-tabs">
        <TabsList>
          <TabsTrigger value="agreement" data-testid="tab-agreement">Agreement</TabsTrigger>
          <TabsTrigger value="emergency" data-testid="tab-emergency">Emergency contact</TabsTrigger>
          <TabsTrigger value="vehicle" data-testid="tab-vehicle">Transportation</TabsTrigger>
          <TabsTrigger value="insurance" data-testid="tab-insurance">Insurance</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents">Report incident</TabsTrigger>
        </TabsList>

        {/* Agreement */}
        <TabsContent value="agreement" className="mt-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2"><FileSignature className="w-5 h-5 text-teal-600" />CLEANBAZ Cleaner Agreement</h3>
                  <p className="text-sm text-slate-600 mt-1">Version {agreement?.version}. Effective {agreement?.effective_at}.</p>
                </div>
                <Badge className={agreementStatus.accepted ? "bg-teal-100 text-teal-800" : "bg-amber-100 text-amber-800"}>
                  {agreementStatus.accepted ? "Accepted" : "Not accepted"}
                </Badge>
              </div>
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                <b>Notice:</b> This agreement is a DRAFT prepared for engineering integration and is subject to review by a licensed Florida attorney before production use. Sections marked <b>[LEGAL REVIEW]</b> particularly require attorney input.
              </div>
              <div className="max-h-64 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 space-y-3" data-testid="agreement-body">
                {agreement?.sections?.map((s) => (
                  <div key={s.key}>
                    <div className="font-semibold text-slate-900">{s.title}</div>
                    <div className="whitespace-pre-wrap">{s.body}</div>
                  </div>
                ))}
              </div>
              {!agreementStatus.accepted && (
                <Button onClick={() => setAgreementOpen(true)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="open-agreement-accept">
                  Review and accept
                </Button>
              )}
              {agreementStatus.accepted && agreementStatus.acceptance && (
                <div className="text-xs text-slate-500">Accepted {agreementStatus.acceptance.accepted_at} · IP {agreementStatus.acceptance.ip || "—"}</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Emergency contact */}
        <TabsContent value="emergency" className="mt-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2"><UserRound className="w-5 h-5 text-teal-600" />Emergency contact</h3>
              <p className="text-sm text-slate-600">CLEANBAZ admin uses this only in the event of an on-job safety incident. Never shared with customers.</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Full name *</Label><Input data-testid="ec-name" value={emergency.name} onChange={ev => setEmergency({ ...emergency, name: ev.target.value })} /></div>
                <div><Label>Relationship</Label><Input data-testid="ec-relationship" value={emergency.relationship} onChange={ev => setEmergency({ ...emergency, relationship: ev.target.value })} placeholder="Spouse, parent, friend" /></div>
                <div><Label>Phone *</Label><Input data-testid="ec-phone" value={emergency.phone} onChange={ev => setEmergency({ ...emergency, phone: ev.target.value })} placeholder="+1 555 555 0100" /></div>
                <div><Label>Email</Label><Input data-testid="ec-email" value={emergency.email} onChange={ev => setEmergency({ ...emergency, email: ev.target.value })} /></div>
              </div>
              <Button onClick={saveEmergency} className="bg-slate-900 text-white" data-testid="ec-save">Save emergency contact</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vehicle */}
        <TabsContent value="vehicle" className="mt-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Car className="w-5 h-5 text-teal-600" />Transportation & vehicle</h3>
              <p className="text-sm text-slate-600">Most CLEANBAZ jobs require you to travel to the customer with supplies and equipment. Choose how you'll reach jobs.</p>
              <div>
                <Label>How will you travel to CLEANBAZ jobs? *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2" data-testid="transport-picker">
                  {Object.entries(TRANSPORT_LABELS).map(([k, v]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setVehicle({ ...vehicle, transportation_type: k, has_vehicle: k === "own_vehicle" })}
                      className={`px-3 py-2 rounded-lg border text-sm transition-colors ${vehicle.transportation_type === k ? "border-teal-500 bg-teal-50 text-teal-800" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}
                      data-testid={`transport-${k}`}
                    >{v}</button>
                  ))}
                </div>
              </div>
              {vehicle.transportation_type === "own_vehicle" && (
                <div className="grid md:grid-cols-3 gap-3 pt-2 border-t border-slate-100" data-testid="vehicle-details">
                  <div>
                    <Label>Vehicle type *</Label>
                    <select
                      value={vehicle.vehicle_type || ""}
                      onChange={ev => setVehicle({ ...vehicle, vehicle_type: ev.target.value })}
                      className="mt-1 w-full h-10 border rounded-md px-3 text-sm"
                      data-testid="vehicle-type"
                    >
                      <option value="">Select…</option>
                      <option value="car">Car</option>
                      <option value="suv">SUV</option>
                      <option value="truck">Truck</option>
                      <option value="van">Van</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div><Label>Year *</Label><Input data-testid="vehicle-year" value={vehicle.year || ""} onChange={ev => setVehicle({ ...vehicle, year: ev.target.value })} placeholder="2020" /></div>
                  <div><Label>Make *</Label><Input data-testid="vehicle-make" value={vehicle.make || ""} onChange={ev => setVehicle({ ...vehicle, make: ev.target.value })} placeholder="Toyota" /></div>
                  <div><Label>Model</Label><Input data-testid="vehicle-model" value={vehicle.model || ""} onChange={ev => setVehicle({ ...vehicle, model: ev.target.value })} placeholder="Corolla" /></div>
                  <div><Label>Color</Label><Input data-testid="vehicle-color" value={vehicle.color || ""} onChange={ev => setVehicle({ ...vehicle, color: ev.target.value })} /></div>
                  <div><Label>Plate state *</Label><Input data-testid="vehicle-plate-state" value={vehicle.plate_state || ""} onChange={ev => setVehicle({ ...vehicle, plate_state: ev.target.value })} placeholder="FL" maxLength={4} /></div>
                  <div><Label>License plate *</Label><Input data-testid="vehicle-plate" value={vehicle.license_plate || ""} onChange={ev => setVehicle({ ...vehicle, license_plate: ev.target.value })} placeholder="ABC1234" /></div>
                  <div className="md:col-span-3">
                    <Label>VIN — last 4 to 6 digits only</Label>
                    <Input data-testid="vehicle-vin" value={vehicle.vin_last6 || ""} onChange={ev => setVehicle({ ...vehicle, vin_last6: ev.target.value })} placeholder="A1B2C3" maxLength={6} />
                    <p className="text-xs text-slate-500 mt-1">Never send a full VIN. CLEANBAZ stores only a partial identifier for verification.</p>
                  </div>

                  {/* Driver's license — CLEANBAZ Step 6.
                      Full DL number is never collected. */}
                  <div className="md:col-span-3 pt-3 border-t border-slate-100" data-testid="dl-section">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2"><UserRound className="w-4 h-4 text-teal-600" />Driver's license</h4>
                      {vehicle.dl_status && (
                        <Badge
                          data-testid="dl-status-badge"
                          className={
                            vehicle.dl_status === "verified" ? "bg-teal-100 text-teal-800 border-teal-200" :
                            vehicle.dl_status === "rejected" ? "bg-red-100 text-red-700 border-red-200" :
                            vehicle.dl_status === "pending"  ? "bg-amber-100 text-amber-800 border-amber-200" :
                            "bg-slate-100 text-slate-600 border-slate-200"
                          }
                        >
                          {vehicle.dl_status.replace(/_/g, " ")}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      CLEANBAZ stores only your state, expiration date, and the last 4 characters of your DL. Never enter your full DL number. Front/back photos are visible only to authorised CLEANBAZ admins.
                    </p>
                  </div>
                  <div><Label>DL state *</Label><Input data-testid="dl-state" value={vehicle.dl_state || ""} onChange={ev => setVehicle({ ...vehicle, dl_state: ev.target.value })} placeholder="FL" maxLength={4} /></div>
                  <div><Label>DL expiration *</Label><Input data-testid="dl-exp" type="date" value={vehicle.dl_expiration_date || ""} onChange={ev => setVehicle({ ...vehicle, dl_expiration_date: ev.target.value })} /></div>
                  <div><Label>DL last 4</Label><Input data-testid="dl-last4" value={vehicle.dl_last4 || ""} onChange={ev => setVehicle({ ...vehicle, dl_last4: ev.target.value })} placeholder="1234" maxLength={4} /></div>
                  <div>
                    <Label>DL front photo</Label>
                    <label className="mt-1 flex items-center gap-2 cursor-pointer px-3 h-10 border rounded-md text-sm text-slate-700 hover:bg-slate-50">
                      <UploadCloud className="w-4 h-4" />
                      {vehicle.dl_front_url ? "Replace front" : "Upload front"}
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={ev => handleUpload(ev, "dl_front")} data-testid="dl-front-file" />
                    </label>
                    {vehicle.dl_front_url && <div className="text-[11px] text-teal-700 mt-1" data-testid="dl-front-preview">Uploaded ✓</div>}
                  </div>
                  <div>
                    <Label>DL back photo</Label>
                    <label className="mt-1 flex items-center gap-2 cursor-pointer px-3 h-10 border rounded-md text-sm text-slate-700 hover:bg-slate-50">
                      <UploadCloud className="w-4 h-4" />
                      {vehicle.dl_back_url ? "Replace back" : "Upload back"}
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={ev => handleUpload(ev, "dl_back")} data-testid="dl-back-file" />
                    </label>
                    {vehicle.dl_back_url && <div className="text-[11px] text-teal-700 mt-1" data-testid="dl-back-preview">Uploaded ✓</div>}
                  </div>
                </div>
              )}
              <Button onClick={saveVehicle} className="bg-slate-900 text-white" data-testid="vehicle-save">Save transportation</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insurance */}
        <TabsContent value="insurance" className="mt-6 space-y-4">
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-teal-600" />Insurance proof</h3>
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                A standard personal auto policy may NOT cover business use. Confirm with your carrier that your policy covers CLEANBAZ activity. CLEANBAZ will block transportation-required jobs whenever your insurance is missing, rejected, or expired. <b>No automatic grace period.</b>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Insurance type</Label>
                  <select value={insForm.insurance_type} onChange={ev => setInsForm({ ...insForm, insurance_type: ev.target.value })} className="mt-1 w-full h-10 border rounded-md px-3 text-sm" data-testid="ins-type">
                    <option value="auto">Auto (personal or commercial)</option>
                    <option value="general_liability">General liability</option>
                    <option value="workers_comp">Workers' compensation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div><Label>Carrier *</Label><Input data-testid="ins-carrier" value={insForm.carrier} onChange={ev => setInsForm({ ...insForm, carrier: ev.target.value })} placeholder="Geico, State Farm, …" /></div>
                <div><Label>Policy number *</Label><Input data-testid="ins-policy" value={insForm.policy_number} onChange={ev => setInsForm({ ...insForm, policy_number: ev.target.value })} placeholder="Full policy number" /></div>
                <div><Label>Effective date</Label><Input data-testid="ins-effective" type="date" value={insForm.effective_date} onChange={ev => setInsForm({ ...insForm, effective_date: ev.target.value })} /></div>
                <div><Label>Expiration date *</Label><Input data-testid="ins-expiration" type="date" value={insForm.expiration_date} onChange={ev => setInsForm({ ...insForm, expiration_date: ev.target.value })} /></div>
                <div>
                  <Label>Proof of insurance document *</Label>
                  <label className="mt-1 flex items-center gap-2 cursor-pointer px-3 h-10 border rounded-md text-sm text-slate-700 hover:bg-slate-50">
                    <UploadCloud className="w-4 h-4" />
                    {insForm.document_url ? "Replace file" : "Upload PDF or image"}
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={ev => handleUpload(ev, "insurance")} data-testid="ins-file" />
                  </label>
                  {insForm.document_url && <div className="text-[11px] text-teal-700 mt-1 truncate" data-testid="ins-file-preview">Uploaded ✓</div>}
                </div>
                <div className="md:col-span-2"><Label>Notes</Label><Textarea data-testid="ins-notes" value={insForm.notes} onChange={ev => setInsForm({ ...insForm, notes: ev.target.value })} placeholder="Commercial rider, business-use endorsement, etc." /></div>
                <label className="md:col-span-2 flex items-start gap-2 p-3 rounded-lg border border-teal-200 bg-teal-50 cursor-pointer" data-testid="ins-attest-wrap">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!insForm.attest_current_accurate}
                    onChange={ev => setInsForm({ ...insForm, attest_current_accurate: ev.target.checked })}
                    data-testid="ins-attest"
                  />
                  <span className="text-xs text-teal-900">
                    I confirm the insurance information and uploaded proof are <b>current and accurate</b>. Submitting false or expired coverage will block me from transportation-required jobs and may end my CLEANBAZ engagement.
                  </span>
                </label>
              </div>
              <Button onClick={submitInsurance} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="ins-submit">Submit for review</Button>
            </CardContent>
          </Card>

          {insurance.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold text-slate-900 mb-3">Your submitted insurance</h4>
                <div className="space-y-2" data-testid="ins-history">
                  {insurance.map(r => {
                    const st = INS_STATUS[r.status] || INS_STATUS.pending_review;
                    return (
                      <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={`ins-row-${r.id}`}>
                        <div>
                          <div className="font-medium text-slate-800">{r.insurance_type} · {r.carrier} · ••••{r.policy_number_last4}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Calendar className="w-3 h-3" /> Expires {r.expiration_date}</div>
                        </div>
                        <Badge className={st.cls}>{st.label}</Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Incidents */}
        <TabsContent value="incidents" className="mt-6 space-y-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Siren className="w-5 h-5 text-red-600" />Report a safety incident</h3>
                <Button onClick={() => setIncidentOpen(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="open-incident-btn">
                  Report incident
                </Button>
              </div>
              <p className="text-sm text-slate-600 mb-4">Vehicle accidents, injuries, property damage, theft accusations, or customer confrontation must be reported within 24 hours. CLEANBAZ admin is alerted immediately.</p>
              {incidents.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No incidents reported.</div>
              ) : (
                <div className="space-y-2" data-testid="incident-history">
                  {incidents.map(i => (
                    <div key={i.id} className="p-3 rounded-lg border border-slate-200 bg-white" data-testid={`incident-row-${i.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-medium text-slate-800">{i.kind.replace(/_/g, " ")} · {i.severity}</div>
                        <Badge className="bg-slate-100 text-slate-700 border-slate-200">{i.status}</Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{i.occurred_at}</div>
                      <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{i.description}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Agreement acceptance dialog */}
      <Dialog open={agreementOpen} onOpenChange={setAgreementOpen}>
        <DialogContent className="max-w-2xl" data-testid="agreement-dialog">
          <DialogHeader>
            <DialogTitle>Accept the CLEANBAZ Cleaner Agreement</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-3 max-h-80 overflow-y-auto pr-2">
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"><b>Notice:</b> Draft — subject to Florida attorney review.</div>
            <div className="text-xs text-teal-800 bg-teal-50 border border-teal-200 rounded p-2" data-testid="agreement-dispute-link">
              This Agreement references the separate <a href="/legal/dispute-policy" target="_blank" rel="noopener noreferrer" className="underline font-semibold">CLEANBAZ Dispute &amp; Damage Policy</a>. It explains how customer complaints and property-damage claims are reviewed. CLEANBAZ does <b>not</b> automatically decide fault or promise reimbursement.
            </div>
            {agreement?.sections?.map(s => (
              <div key={s.key}><div className="font-semibold">{s.title}</div><div className="whitespace-pre-wrap">{s.body}</div></div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAgreementOpen(false)} data-testid="agreement-cancel">Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={acceptAgreement} data-testid="agreement-accept-btn">
              I have read and accept this Agreement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incident dialog */}
      <Dialog open={incidentOpen} onOpenChange={setIncidentOpen}>
        <DialogContent className="max-w-xl" data-testid="incident-dialog">
          <DialogHeader><DialogTitle>Report incident</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kind</Label>
                <select value={incidentForm.kind} onChange={ev => setIncidentForm({ ...incidentForm, kind: ev.target.value })} className="mt-1 w-full h-10 border rounded-md px-3 text-sm" data-testid="inc-kind">
                  <option value="vehicle_accident">Vehicle accident</option>
                  <option value="injury">Injury</option>
                  <option value="property_damage">Property damage</option>
                  <option value="customer_incident">Customer incident</option>
                  <option value="theft">Theft accusation</option>
                  <option value="safety_hazard">Safety hazard</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label>Severity</Label>
                <select value={incidentForm.severity} onChange={ev => setIncidentForm({ ...incidentForm, severity: ev.target.value })} className="mt-1 w-full h-10 border rounded-md px-3 text-sm" data-testid="inc-severity">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div><Label>Occurred at</Label><Input type="datetime-local" value={incidentForm.occurred_at} onChange={ev => setIncidentForm({ ...incidentForm, occurred_at: ev.target.value })} data-testid="inc-when" /></div>
              <div><Label>Job ID (optional)</Label><Input value={incidentForm.job_id} onChange={ev => setIncidentForm({ ...incidentForm, job_id: ev.target.value })} data-testid="inc-job" /></div>
              <div className="col-span-2"><Label>Location</Label><Input value={incidentForm.location} onChange={ev => setIncidentForm({ ...incidentForm, location: ev.target.value })} placeholder="Address or intersection" data-testid="inc-loc" /></div>
              <div className="col-span-2"><Label>Description *</Label><Textarea rows={4} value={incidentForm.description} onChange={ev => setIncidentForm({ ...incidentForm, description: ev.target.value })} placeholder="What happened, in your own words" data-testid="inc-desc" /></div>
              <div><Label>Involved parties</Label><Input value={incidentForm.involved_parties} onChange={ev => setIncidentForm({ ...incidentForm, involved_parties: ev.target.value })} data-testid="inc-parties" /></div>
              <div><Label>Police report #</Label><Input value={incidentForm.police_report_number} onChange={ev => setIncidentForm({ ...incidentForm, police_report_number: ev.target.value })} data-testid="inc-police" /></div>
              <div className="col-span-2"><Label>Insurance claim #</Label><Input value={incidentForm.insurance_claim_number} onChange={ev => setIncidentForm({ ...incidentForm, insurance_claim_number: ev.target.value })} data-testid="inc-claim" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncidentOpen(false)} data-testid="inc-cancel">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={submitIncident} data-testid="inc-submit">Submit report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
